// Copyright ©️ 2026 eVolve MEP, LLC

import { type Task, useEvolveGet, useEvolvePost } from '@evolvemep/api-ts';
import { notifications } from '@mantine/notifications';

/**
 * Manages task completion, including a confirmation step when completing the task would also complete its work order.
 *
 * Call `attemptComplete` when the user clicks "Complete". If the task is the final task for its work order,
 * returns `false` so the caller can render a confirmation modal. Once the user confirms, call `completeTask`
 * directly to finish the operation
 *
 * @param task - The task to complete. `taskId` and `taskName` are required.
 * @param onTaskUpdated - Optional callback invoked with the updated task after a successful completion.
 */
export const useCompleteTask = (task: Pick<Task, 'taskId' | 'taskName'>, onTaskUpdated?: (t: Task) => void) => {
  const { apiCall: checkWillCompleteWO, loading: checkingWillCompleteWO } = useEvolveGet(
    `shop/task/${task.taskId}/isFinalTaskForWorkOrder`,
    { alertOnError: true },
  );
  const { apiCall: completeTaskApiCall, loading: completingTask } = useEvolvePost(`shop/task/${task.taskId}/complete`);

  const completeTask = async () => {
    const updatedTask = await completeTaskApiCall({});
    notifications.show({
      title: 'Successfully completed',
      message: `Completed task ${task.taskName}.`,
      color: 'green',
    });
    onTaskUpdated?.(updatedTask);
  };

  const attemptComplete = async () => {
    const willCompleteWorkOrder = await checkWillCompleteWO();
    if (willCompleteWorkOrder) {
      return false;
    }
    await completeTask();
    return true;
  };

  return {
    attemptComplete,
    completeTask,
    loading: checkingWillCompleteWO || completingTask,
  };
};
